/* ==========================================================================
   pv-cart-rules — client half of the cart rules enforced server-side by the
   proffvolt-cart-rules validation function.

   1) Klargjøring reconciler: any cart line whose product requires a
      companion (custom.required_together, e.g. aggregat -> klargjøring)
      gets that companion auto-added and quantity-synced. Removing the fee
      line snaps it back. Truth is read from the server-rendered state div
      in snippets/pv-cart-rules-state.liquid (via GET /cart?sections=mini-cart)
      so every add path (PDP, quick-add, sticky bar, mini-cart) converges.

   2) B2B declaration form (cart page): persists b2b_firmanavn / b2b_orgnr /
      b2b_bekreftet as cart attributes via /cart/update.js. The validation
      function blocks checkout until they're present and the org.nr passes
      the MOD11 check — this form is the way through.

   Loaded once from layout/theme.liquid. No dependencies beyond pubsub.js
   (optional) and the mini-cart element (optional).
   ========================================================================== */
(function () {
  var routes = window.routes || {};
  var CART_URL = routes.cart_url || '/cart';
  var UPDATE_URL = routes.cart_update_url || '/cart/update.js';
  var ADD_URL = routes.cart_add_url ? routes.cart_add_url + '.js' : '/cart/add.js';
  var syncing = false;

  function parseMap(str) {
    var map = {};
    (str || '').split(',').forEach(function (pair) {
      if (!pair) return;
      var parts = pair.split(':');
      var id = parseInt(parts[0], 10);
      var qty = parseInt(parts[1], 10);
      if (id && qty >= 0) map[id] = (map[id] || 0) + qty;
    });
    return map;
  }

  function onCartPage() {
    return window.location.pathname.indexOf(CART_URL) === 0;
  }

  async function fetchState() {
    // NB: must be the root sections endpoint — /cart?sections=… with an
    // Accept: application/json header returns the cart JSON, not sections.
    var res = await fetch('/?sections=mini-cart');
    if (!res.ok) throw new Error('pv-cart-rules: state fetch ' + res.status);
    var sections = await res.json();
    var doc = new DOMParser().parseFromString(sections['mini-cart'] || '', 'text/html');
    return doc.querySelector('[data-pv-cart-rules]');
  }

  function repaintMiniCart(data) {
    var mini = document.querySelector('mini-cart');
    if (!mini || !data || !data.sections) return;
    mini.getSectionsToRender().forEach(function (section) {
      var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (el && data.sections[section.id]) {
        el.innerHTML = mini.getSectionInnerHTML(data.sections[section.id], section.selector);
      }
    });
    if (typeof mini.rebindAfterRender === 'function') mini.rebindAfterRender();
  }

  function sectionIds() {
    var mini = document.querySelector('mini-cart');
    return mini && typeof mini.getSectionsToRender === 'function'
      ? mini.getSectionsToRender().map(function (s) { return s.id; }).join(',')
      : '';
  }

  // additions must go through /cart/add.js — /cart/update.js only changes
  // lines already in the cart; qty changes and removals stay on update.js.
  async function applyUpdates(updates, present) {
    var adds = [];
    var changes = {};
    Object.keys(updates).forEach(function (id) {
      if (!(id in present) && updates[id] > 0) adds.push({ id: parseInt(id, 10), quantity: updates[id] });
      else changes[id] = updates[id];
    });
    var headers = { 'Content-Type': 'application/json;charset=UTF-8', Accept: 'application/json' };
    var data = null;
    if (adds.length) {
      var resAdd = await fetch(ADD_URL, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ items: adds, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      data = await resAdd.json();
    }
    if (Object.keys(changes).length) {
      var resUpd = await fetch(UPDATE_URL, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ updates: changes, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      data = await resUpd.json();
    }
    repaintMiniCart(data);
    return data;
  }

  function domStateSaysIrrelevant() {
    // The state div ships inside the mini-cart markup on every render. If the
    // freshly painted DOM shows no required-together lines, no fee lines and
    // no b2b flag, there is nothing to reconcile — skip the network entirely
    // (this is the common case for every normal pageview and cart change).
    var el = document.querySelector('[data-pv-cart-rules]');
    if (!el) return false; // no state visible -> fall through to server truth
    return !el.getAttribute('data-required') && !el.getAttribute('data-fees');
  }

  async function sync() {
    if (syncing) return;
    syncing = true;
    var applied = false;
    try {
      // Let the theme finish painting the fresh sections (product-form paints
      // right after publishing cartUpdate), then consult the DOM state first.
      await new Promise(function (r) { setTimeout(r, 350); });
      if (domStateSaysIrrelevant()) { syncing = false; return; }
      // Up to 3 passes: state is re-fetched after each update so concurrent
      // changes converge on the server truth instead of fighting it.
      for (var i = 0; i < 3; i++) {
        var el = await fetchState();
        if (!el) break;
        var needed = parseMap(el.getAttribute('data-required'));
        var present = parseMap(el.getAttribute('data-fees'));
        var updates = {};
        Object.keys(needed).forEach(function (id) {
          if ((present[id] || 0) !== needed[id]) updates[id] = needed[id];
        });
        Object.keys(present).forEach(function (id) {
          if (!(id in needed)) updates[id] = 0; // orphan fee line -> remove
        });
        if (Object.keys(updates).length === 0) break;
        await applyUpdates(updates, present);
        applied = true;
      }
    } catch (e) {
      console.error('pv-cart-rules: sync failed', e);
    } finally {
      syncing = false;
    }
    // The cart page renders its own line items server-side; after a fix the
    // cheapest honest repaint is a reload. Only happens when something was
    // actually corrected (rare: generator qty change / tampered fee line).
    // sessionStorage guard caps it so a persistent mismatch (e.g. the fee
    // variant becoming unsellable) can never reload-loop the page.
    if (applied && onCartPage()) {
      var now = Date.now();
      var last = parseInt(sessionStorage.getItem('pvCrReloadTs') || '0', 10);
      var count = parseInt(sessionStorage.getItem('pvCrReloadN') || '0', 10);
      if (now - last > 60000) count = 0;
      if (count < 2) {
        sessionStorage.setItem('pvCrReloadTs', String(now));
        sessionStorage.setItem('pvCrReloadN', String(count + 1));
        window.location.reload();
      }
    }
  }

  /* ---------------- B2B declaration form (cart page) ---------------- */
  var b2bTimer = null;

  function orgnrValid(raw) {
    var digits = (raw || '').replace(/\s/g, '');
    if (!/^\d{9}$/.test(digits)) return false;
    var weights = [3, 2, 7, 6, 5, 4, 3, 2];
    var sum = 0;
    for (var i = 0; i < 8; i++) sum += weights[i] * Number(digits[i]);
    var control = 11 - (sum % 11);
    if (control === 11) control = 0;
    return control !== 10 && control === Number(digits[8]);
  }

  function b2bStatus(form, msg, ok) {
    var el = form.querySelector('[data-pv-b2b-status]');
    if (!el) return;
    el.textContent = msg;
    el.classList.toggle('pv-b2b__status--ok', !!ok);
    el.classList.toggle('pv-b2b__status--warn', !ok);
  }

  function saveB2b(form) {
    var firmanavn = (form.querySelector('[name="pv_b2b_firmanavn"]') || {}).value || '';
    var orgnr = (form.querySelector('[name="pv_b2b_orgnr"]') || {}).value || '';
    var bekreftetEl = form.querySelector('[name="pv_b2b_bekreftet"]');
    var bekreftet = bekreftetEl && bekreftetEl.checked ? 'ja' : '';

    if (orgnr && !orgnrValid(orgnr)) {
      b2bStatus(form, 'Organisasjonsnummeret ser ikke gyldig ut (9 siffer).', false);
    }

    fetch(UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=UTF-8', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { b2b_firmanavn: firmanavn.trim(), b2b_orgnr: orgnr.trim(), b2b_bekreftet: bekreftet } }),
    })
      .then(function (res) { return res.json(); })
      .then(function () {
        if (firmanavn.trim() && orgnrValid(orgnr) && bekreftet === 'ja') {
          b2bStatus(form, 'Bedriftsinformasjon lagret — du kan gå til kassen.', true);
        } else if (!orgnr || orgnrValid(orgnr)) {
          b2bStatus(form, 'Alle feltene må fylles ut før kjøpet kan fullføres.', false);
        }
      })
      .catch(function (e) { console.error('pv-cart-rules: b2b save failed', e); });
  }

  document.addEventListener('input', function (evt) {
    var form = evt.target && evt.target.closest && evt.target.closest('[data-pv-b2b-form]');
    if (!form) return;
    clearTimeout(b2bTimer);
    b2bTimer = setTimeout(function () { saveB2b(form); }, 500);
  });
  document.addEventListener('change', function (evt) {
    var form = evt.target && evt.target.closest && evt.target.closest('[data-pv-b2b-form]');
    if (!form) return;
    clearTimeout(b2bTimer);
    saveB2b(form);
  });

  /* ---------------- hooks ---------------- */
  document.addEventListener('DOMContentLoaded', function () {
    sync();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        if (data && data.source === 'pv-cart-rules') return;
        sync();
      });
    }
  });

  window.PVCartRules = { sync: sync };
})();
